import React, { useRef, useState } from 'react';
import {
  Users as UsersIcon,
  ShieldCheck,
  Clock3,
  AlertTriangle,
  Sparkles,
  X,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import UserCard from '../components/usercard';
import PendingCard from '../components/pendingCard';


const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-white/5 rounded-2xl ${className}`} />
);

const Users = () => {
  const token = localStorage.getItem('token');
  const baseUrl = 'https://api.codedmastery.com';
  // const baseUrl = 'http://localhost:5000';


  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const modalRef = useRef(null);

  // endpoints mapping
  const endpoints = {
    allUsers: `${baseUrl}/auth/all-users`,
    pendingUsers: `${baseUrl}/users/un_approved_users`,
    approveUser: (id) => `${baseUrl}/auth/approve/${id}`,
    blockUser: (id) => `${baseUrl}/auth/block/${id}`,
  };

  const queryClient = useQueryClient();

  const { data: allUsers, isLoading: loadingAll, error: errorAll } = useQuery({
    queryKey: ['users', 'all'],
    fetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 10, // 10 minutes
    queryFn: async () => {
      const res = await fetch(`${baseUrl}/auth/all-users`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!res.ok) throw new Error('Failed to fetch users');
      const newData = await res.json();
      return newData.users || [];
    },
  });

  const { data: pendingUsers, isLoading: loadingPending, error: errorPending } = useQuery({
    queryKey: ['users', 'pending'],
    fetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 10, // 10 minutes
    queryFn: async () => {
      const res = await fetch(endpoints.pendingUsers, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });
      if (!res.ok) throw new Error('Failed to fetch pending users');
      const data = await res.json();
      return data.users || [];
    },
  });
  // console.log('Fetched users:', allUsers);



  const handleSearch = () => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
      setSearchResults([]);
      setIsSearchModalOpen(true);
      return;
    }

    const filteredUsers = (allUsers || []).filter((user) => {
      const name = (user.name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });

    setSearchResults(filteredUsers);
    setIsSearchModalOpen(true);
  };


  const approveMutation = useMutation({
    mutationFn: async (user) => {
      // console.log('Approving user:', user);
      const res = await fetch(endpoints.approveUser(user.uid), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          subScription: user.subscription || 'basic',
        }),
      });
      if (!res.ok) throw new Error('Approve failed');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });


  const blockMutation = useMutation({
    mutationFn: async (user) => {
      const res = await fetch(endpoints.blockUser(user.uid), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },

      });
      if (!res.ok) throw new Error('Block failed');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });



  const gradient = (
    <div className="absolute inset-0 -z-10">
      <div className="absolute -top-24 -left-24 h-72 w-72 bg-purple-600/30 blur-3xl" />
      <div className="absolute top-10 right-0 h-72 w-72 bg-cyan-500/25 blur-3xl" />
      <div className="absolute bottom-0 left-1/3 h-64 w-64 bg-lime-400/15 blur-3xl" />
    </div>
  );




  return (
    <div className="relative min-h-screen overflow-hidden">
      {gradient}
      <main className="px-6 lg:px-10 py-6 lg:py-10 space-y-6">
       
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Coded Mastery Inc</p>
            <h1 className="text-3xl font-semibold text-white flex items-center gap-3">
              <Sparkles className="h-7 w-7 text-cyan-300" />
              User Management
            </h1>
            <p className="text-sm text-slate-400">Manage all platform users and pending approvals.</p>
          </div>
          
          <div className="flex items-center gap-3 px-4 py-2 rounded-2xl glass-panel border border-white/10 w-full lg:w-auto">
            <input
              className="bg-white/5 w-full border-none placeholder:text-slate-500 text-slate-100 focus:border-none focus:ring-0 focus:outline-none"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
            <button
              type="button"
              onClick={handleSearch}
              className="rounded-xl bg-cyan-500/20 px-3 py-2 text-sm font-medium text-cyan-100 transition hover:bg-cyan-500/30"
            >
              Search
            </button>
          </div>
          <div className="flex items-center gap-3 px-4 py-2 rounded-2xl glass-panel border border-white/10">
            <Clock3 className="h-4 w-4 text-cyan-200" />
            <span className="text-xs text-slate-300">Live synced </span>
          </div>
        </div>

        {(errorAll || errorPending) && (
          <div className="glass-panel rounded-2xl p-4 border border-rose-400/30 bg-rose-500/10 text-rose-100 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5" />
            <p>{errorAll?.message || errorPending?.message}</p>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          <div className="xl:col-span-2 glass-panel rounded-3xl p-5 border border-white/10 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <UsersIcon className="h-5 w-5 text-cyan-200" />
                <p className="text-sm text-slate-200">All Users</p>
              </div>
              <span className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10 text-slate-200">
                {allUsers?.length || 0} total
              </span>
            </div>
            {loadingAll ? (
              <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-32" />
                ))}
              </div>
            ) : (
              <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4">
                {allUsers?.length ? (
                  allUsers.map((user) => <UserCard key={user._id || user.email} user={user} />)
                ) : (
                  <p className="text-slate-400 text-sm">No users found.</p>
                )}
              </div>
            )}
          </div>

          <div className="glass-panel rounded-3xl p-5 border border-white/10 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-cyan-200" />
                <p className="text-sm text-slate-200">Waiting Approval</p>
              </div>
              <span className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10 text-slate-200">
                {pendingUsers?.length || 0} pending
              </span>
            </div>
            {loadingPending ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-24" />
                ))}
              </div>
            ) : pendingUsers?.length ? (
              <div className="space-y-3">
                {pendingUsers.map((user) => (
                  <PendingCard
                    key={user._id || user.email}
                    user={user}
                    onApprove={(u) => approveMutation.mutate(u)}
                    onBlock={(u) => blockMutation.mutate(u)}
                    isMutating={approveMutation.isPending || blockMutation.isPending}
                  />
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-sm">No approvals pending.</p>
            )}
          </div>
        </div>
      </main>

      {isSearchModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-sm">
          <div ref={modalRef} className="w-full max-w-lg rounded-3xl border border-white/10 bg-slate-900/95 p-5 shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-white">Search results</p>
                <p className="text-sm text-slate-400">
                  {searchResults.length ? 'Matched users' : 'No matching users found'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsSearchModalOpen(false)}
                className="rounded-full p-2 text-slate-300 transition hover:bg-white/10 hover:text-white"
                aria-label="Close search results"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 max-h-[60vh] overflow-y-auto pr-1">
              {searchResults.length ? (
                searchResults.map((user) => (
                  <div key={user._id || user.email} className="mb-3">
                    <UserCard user={user} />
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-white/10 bg-white/5 p-6 text-center text-sm text-slate-400">
                  Try another name or email address.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
